import type { SurveyDetailsV2Dto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Button } from "@/components/Button/Button";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { isDefined } from "@/helpers/util";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { useConfig } from "@/providers/config/useConfig";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { SurveyActionSection } from "./components/SurveyActionSection";
import { SurveyExportModal } from "./components/SurveyExportModal";
import { SurveyInfoSection } from "./components/SurveyInfoSection";
import { SurveyQuestionResponseCard } from "./components/SurveyQuestionResponseCard";

export interface LayoutProps {
  survey: SurveyDetailsV2Dto;
  isFinished: boolean;
  onDelete: ({ surveyId }: { surveyId: string }) => Promise<unknown>;
}

export function Layout({ survey, isFinished, onDelete }: LayoutProps): React.ReactNode {
  const [isSurveyExportModalOpened, surveyExportModalOpenHandlers] = useBool();

  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal("survey-delete-modal");
  const isSurveyFollowUpActionsAvailable = useConfig("isSurveyFollowUpActionsAvailable");
  const slug = useSlug();
  const { t } = useTranslation();

  const isFollowUpActionsAvailable = isFinished && isSurveyFollowUpActionsAvailable;
  const allQuestions = survey.questionGroups.flatMap((group) => group.questions);

  return (
    <DocumentPaper
      title={t("page.survey-details.survey-title")}
      subTitle={
        <Breadcrumbs
          pages={[
            {
              name: t("page.surveys.list.title"),
              to: routes.surveys.list({ slug }),
            },
            {
              name: t("page.survey-details.survey-title"),
            },
          ]}
        />
      }
      actions={
        <>
          {survey.canDelete && (
            <Button data-testid="survey-delete-btn" styling="danger" onClick={openDeleteModal}>
              {t("page.survey-details.actions.delete")}
            </Button>
          )}
          {isDefined(survey.answerCount) && survey.answerCount > 0 && (
            <Button onClick={surveyExportModalOpenHandlers.setTrue} styling="secondary">
              {t("page.survey-details.actions.export")}
            </Button>
          )}
          {survey.canEdit && (
            <Button data-testid="survey-edit-btn" type="link" href={routes.surveys.edit({ slug, id: survey.id })}>
              {t("page.survey-details.actions.edit")}
            </Button>
          )}
        </>
      }
    >
      <PageGrid.Grid>
        <PageGrid.Item size="66%">
          <div className="flex w-full flex-col gap-4">
            <SurveyInfoSection showDuration showStatus {...{ survey }} />

            {/* Mobile */}
            {isFollowUpActionsAvailable && (
              <div className="block xl:hidden">
                <SurveyActionSection survey={survey} />
              </div>
            )}

            <div className="flex w-full flex-col gap-4">
              {allQuestions.map((question) => (
                <SurveyQuestionResponseCard
                  key={question.id}
                  canViewResults={survey.canViewResults}
                  isSurveyEnded={isFinished}
                  {...{ question }}
                />
              ))}
            </div>
          </div>
        </PageGrid.Item>
        {/* Desktop */}
        {isFollowUpActionsAvailable && (
          <PageGrid.Item size="33%">
            <div className="hidden xl:block">
              <SurveyActionSection survey={survey} />
            </div>
          </PageGrid.Item>
        )}
      </PageGrid.Grid>

      <DeleteModal
        title={t("page.surveys.actions.delete.title")}
        description={t("page.surveys.actions.delete.description")}
        onDelete={() => onDelete({ surveyId: survey.id })}
        deleteBtnProps={{
          "data-testid": "survey-action-delete",
        }}
        {...deleteModalProps}
      />
      <SurveyExportModal
        isOpened={isSurveyExportModalOpened}
        onOpenChange={surveyExportModalOpenHandlers.set}
        surveyId={survey.id}
        surveyName={survey.title}
      />
    </DocumentPaper>
  );
}
